// Brand-driven podcast cover graphic. No photo of Manuel.
// Mirrors the show's visual identity: red ♀ and blue ♂ flanking a central mic,
// halftone dot pattern, audio waveform, big condensed typography.
import { Mic } from "lucide-react";

export default function PodcastCover({ episodeTag = "Season 2 · Coming Soon" }) {
  return (
    <div
      data-testid="podcast-cover"
      className="relative w-full aspect-square overflow-hidden bg-[#0A0A0A]"
    >
      {/* Halftone dot ambience */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(225,6,0,0.7) 1.2px, transparent 1.5px)",
          backgroundSize: "14px 14px",
          backgroundPosition: "left top",
          maskImage: "linear-gradient(to right, black 0%, transparent 55%)",
          WebkitMaskImage: "linear-gradient(to right, black 0%, transparent 55%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(30,111,224,0.7) 1.2px, transparent 1.5px)",
          backgroundSize: "14px 14px",
          backgroundPosition: "right top",
          maskImage: "linear-gradient(to left, black 0%, transparent 55%)",
          WebkitMaskImage: "linear-gradient(to left, black 0%, transparent 55%)",
        }}
      />

      {/* Red glow left, blue glow right */}
      <div
        className="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #E10600 0%, transparent 65%)" }}
      />
      <div
        className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #1E6FE0 0%, transparent 65%)" }}
      />

      {/* Corner stripes — red top-left, blue bottom-right */}
      <div className="absolute top-0 left-0 h-1 w-16 bg-[#E10600]" />
      <div className="absolute top-0 left-0 w-1 h-16 bg-[#E10600]" />
      <div className="absolute bottom-0 right-0 h-1 w-16 bg-[#1E6FE0]" />
      <div className="absolute bottom-0 right-0 w-1 h-16 bg-[#1E6FE0]" />

      {/* Mic + ♀ ♂ symbols cluster */}
      <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
        <div className="flex items-center gap-2 mb-3">
          <span
            aria-hidden
            className="font-black text-5xl leading-none text-[#E10600]"
          >
            ♀
          </span>
          <span className="w-16 h-16 md:w-20 md:h-20 bg-white flex items-center justify-center">
            <Mic className="w-8 h-8 md:w-10 md:h-10 text-black" />
          </span>
          <span
            aria-hidden
            className="font-black text-5xl leading-none text-[#1E6FE0]"
          >
            ♂
          </span>
        </div>

        <p className="mt-4 text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 font-mono">
          {episodeTag}
        </p>

        <h2 className="mt-4 font-display text-5xl md:text-6xl uppercase leading-[0.85] text-white">
          Prove<br />
          <span className="text-[#1E6FE0]">Your</span>{" "}
          <span className="text-[#E10600]">Point</span>
        </h2>

        <p className="mt-4 text-[10px] tracking-[0.3em] uppercase font-mono text-zinc-500">
          Hosted by Manuel Santana
        </p>
      </div>

      {/* Bottom waveform */}
      <div className="absolute bottom-3 left-4 right-4 flex items-end gap-[2px] h-5 opacity-80">
        {Array.from({ length: 56 }).map((_, i) => {
          const accent = i % 2 === 0 ? "#E10600" : "#1E6FE0";
          const seed = Math.abs(Math.sin(i * 0.55));
          return (
            <span
              key={i}
              className="flex-1"
              style={{
                background: accent,
                height: `${15 + seed * 85}%`,
                opacity: i % 4 === 0 ? 1 : 0.55,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
